import { Product, TagTypes } from "../commonTypes";
import * as productsDBActions from "../modules/@supabase/products";
import {
  ProductFilters,
  PaginatedProductsResult,
} from "../modules/@supabase/products";

export namespace ProductService {
  /**
   * Internal functions
   */

  function enrichProducts(products: Product[]): Product[] {
    return products.map((product) => {
      let brand = "";

      let filter_tags = product.product_tags.map(({ tags }) => {
        if (tags.type == TagTypes.BRAND) {
          brand = tags.name;
        }
        return tags.slug;
      });

      let totalAvailability = 0;

      let price = 0;
      product.product_variants.forEach((variant) => {
        if (variant.color) {
          filter_tags.push(variant.color.slug);
        }

        if (typeof variant.stock === "object") {
          variant.stock = 0;
        }

        if (variant.stock < 0) {
          // Ensure stock can't go below 0
          variant.stock = 0;
        }

        totalAvailability += variant.stock;

        if (variant.size) {
          filter_tags.push(variant.size.slug);

          if (price < product.rrp) {
            price = product.rrp;
          }
        }
      });

      if (totalAvailability > 0) {
        filter_tags.push("product-available");
      }

      return { ...product, filter_tags, brand, price, totalAvailability };
    });
  }

  /**
   * Can also get products that have been unpublished
   */
  export async function getProductById(id: number): Promise<Product> {
    return productsDBActions.getProductById(id);
  }

  export async function getProducts(): Promise<Product[]> {
    const products = await productsDBActions.getAllProducts();

    // Enrich the products

    return enrichProducts(products);
  }

  /**
   * Get products by their IDs - used for cart validation
   */
  export async function getProductsByIds(ids: number[]): Promise<Product[]> {
    if (!ids || ids.length === 0) return [];

    const products = await productsDBActions.getProductsByIds(ids);
    return enrichProducts(products);
  }

  /**
   * Get a single product by slug - used for product detail page SSG
   */
  export async function getProductBySlug(slug: string): Promise<Product | null> {
    const product = await productsDBActions.getProductBySlug(slug);
    if (!product) return null;

    return enrichProducts([product])[0];
  }

  /**
   * Get recommended products by concept tag - used for "You might also like"
   */
  export async function getRecommendedProducts(
    conceptTagSlug: string,
    excludeProductId: number,
    limit: number = 4
  ): Promise<Product[]> {
    const products = await productsDBActions.getRecommendedProducts(
      conceptTagSlug,
      excludeProductId,
      limit
    );
    return enrichProducts(products);
  }

  /**
   * Get products by variant IDs - used for Statistics
   */
  export async function getProductsByVariantIds(
    variantIds: number[]
  ): Promise<Product[]> {
    if (!variantIds || variantIds.length === 0) return [];

    const products = await productsDBActions.getProductsByVariantIds(variantIds);
    return enrichProducts(products);
  }

  export type PaginatedProductsResponse = Omit<
    PaginatedProductsResult,
    "products"
  > & {
    products: Product[];
  };

  /**
   * Get paginated products with filtering and search
   * Products are enriched with filter_tags, brand, price, and totalAvailability
   */
  export async function getPaginatedProducts(
    filters: ProductFilters = {},
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedProductsResponse> {
    const result = await productsDBActions.getPaginatedProducts(
      filters,
      page,
      pageSize
    );

    return {
      ...result,
      products: enrichProducts(result.products),
    };
  }
}
